import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { Loader2, Pencil, Plus, SlidersHorizontal, Trash2 } from 'lucide-react';
import type { ApiError } from '@pms/shared';
import {
  financeService, type PurchaseCategory, type PaymentMode,
  type BudgetRow, type FinanceAuditLog,
} from '@/services/finance.service';
import { Tabs } from '@/components/ui/tabs';
import { DataTable, type Column } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { formatINR } from '@/lib/currency';
import { formatDateTime } from '@/lib/dateFormat';
import { cn } from '@/lib/utils';
import { CategoryFormDialog } from './components/CategoryFormDialog';

function errMsg(e: unknown): string {
  return (e as AxiosError<ApiError>).response?.data?.error?.message ?? 'Something went wrong';
}

const TABS = [
  { key: 'categories', label: 'Categories' },
  { key: 'modes', label: 'Payment Modes' },
  { key: 'rules', label: 'System Rules' },
  { key: 'budget', label: 'Budget' },
  { key: 'audit', label: 'Audit Logs' },
];

/** Thin wrapper: the System Rules rows read `on={...}` and carry their own label text. */
function Toggle({ on, onChange, disabled, label }: { on: boolean; onChange: (v: boolean) => void; disabled?: boolean; label: string }) {
  return <Switch checked={on} onChange={onChange} disabled={disabled} aria-label={label} />;
}

// ─── Categories ─────────────────────────────────────────────────────────────────

function CategoriesTab() {
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<{ mode: 'create' | 'edit'; category?: PurchaseCategory } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const { data = [], isLoading } = useQuery({
    queryKey: ['finance', 'categories'],
    queryFn: () => financeService.listCategories(),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => financeService.deleteCategory(id),
    onSuccess: () => {
      toast.success('Category deleted');
      void queryClient.invalidateQueries({ queryKey: ['finance', 'categories'] });
      void queryClient.invalidateQueries({ queryKey: ['finance', 'budget'] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const columns: Column<PurchaseCategory>[] = [
    { key: 'name', header: 'Name', cell: (r) => <span className="font-medium">{r.name}</span> },
    { key: 'budget', header: 'Monthly Budget', className: 'text-right', cell: (r) => formatINR(r.monthly_budget) },
    { key: 'max', header: 'Max Limit', className: 'text-right', cell: (r) => formatINR(r.max_purchase_limit) },
    { key: 'gst', header: 'GST', cell: (r) => (r.gst_applicable ? 'Yes' : 'No') },
    { key: 'status', header: 'Status', cell: (r) => <Badge variant={r.is_active ? 'success' : 'secondary'}>{r.is_active ? 'Active' : 'Inactive'}</Badge> },
    {
      key: 'actions', header: 'Actions', className: 'w-24',
      cell: (r) => (
        <div className="flex items-center gap-0.5">
          <button type="button" onClick={() => setDialog({ mode: 'edit', category: r })} className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
          <button type="button" onClick={() => setDeleteTarget({ id: r.id, name: r.name })} className="rounded p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold">Purchase Categories</h2>
        <Button onClick={() => setDialog({ mode: 'create' })}><Plus className="h-4 w-4" /> Add</Button>
      </div>
      <DataTable columns={columns} rows={data} rowKey={(r) => r.id} loading={isLoading} empty="No categories found" />

      {dialog && (
        <CategoryFormDialog open mode={dialog.mode} category={dialog.category} onClose={() => setDialog(null)} onSuccess={() => setDialog(null)} />
      )}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Category</AlertDialogTitle>
            <AlertDialogDescription>Delete &quot;{deleteTarget?.name}&quot;? Existing purchases keep their category reference.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => { if (deleteTarget) { deleteMut.mutate(deleteTarget.id); setDeleteTarget(null); } }}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ─── Payment Modes ────────────────────────────────────────────────────────────────

function PaymentModesTab() {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const { data = [], isLoading } = useQuery({
    queryKey: ['finance', 'payment-modes', 'all'],
    queryFn: () => financeService.listAllPaymentModes(),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['finance', 'payment-modes'] });
  };

  const addMut = useMutation({
    mutationFn: (mode_name: string) => financeService.createPaymentMode({ mode_name }),
    onSuccess: () => { toast.success('Payment mode added'); setName(''); invalidate(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const toggleMut = useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) => financeService.updatePaymentMode(id, { is_active }),
    onSuccess: invalidate,
    onError: (e) => toast.error(errMsg(e)),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => financeService.deletePaymentMode(id),
    onSuccess: () => { toast.success('Payment mode removed'); invalidate(); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const columns: Column<PaymentMode>[] = [
    { key: 'name', header: 'Mode Name', cell: (r) => <span className="font-medium">{r.mode_name}</span> },
    {
      key: 'status', header: 'Status',
      cell: (r) => (
        <button type="button" onClick={() => toggleMut.mutate({ id: r.id, is_active: !r.is_active })}>
          <Badge variant={r.is_active ? 'success' : 'secondary'}>{r.is_active ? 'Active' : 'Inactive'}</Badge>
        </button>
      ),
    },
    {
      key: 'actions', header: 'Actions', className: 'w-20',
      cell: (r) => (
        <button type="button" onClick={() => setDeleteTarget({ id: r.id, name: r.mode_name })} disabled={!r.is_active} className="rounded p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:opacity-40" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
      ),
    },
  ];

  return (
    <>
      <h2 className="mb-4 text-lg font-semibold">Payment Modes</h2>
      <div className="mb-4 flex gap-2">
        <Input placeholder="New mode name" value={name} onChange={(e) => setName(e.target.value)} className="max-w-xs" />
        <Button onClick={() => name.trim() && addMut.mutate(name.trim())} disabled={addMut.isPending || !name.trim()}>
          {addMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add
        </Button>
      </div>
      <DataTable columns={columns} rows={data} rowKey={(r) => r.id} loading={isLoading} empty="No payment modes." />

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Payment Mode</AlertDialogTitle>
            <AlertDialogDescription>Remove &quot;{deleteTarget?.name}&quot;?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => { if (deleteTarget) { deleteMut.mutate(deleteTarget.id); setDeleteTarget(null); } }}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ─── System Rules ─────────────────────────────────────────────────────────────────

function RuleRow({ title, hint, children }: { title: string; hint: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between rounded-xl border bg-card p-4">
      <div>
        <p className="font-medium">{title}</p>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>
      {children}
    </div>
  );
}

function SystemRulesTab() {
  const queryClient = useQueryClient();
  const { data: rules } = useQuery({
    queryKey: ['finance', 'rules'],
    queryFn: () => financeService.getRules(),
  });

  const mut = useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) => financeService.setRule(key, value),
    onSuccess: () => { toast.success('Rule updated'); void queryClient.invalidateQueries({ queryKey: ['finance', 'rules'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const r = rules ?? {};
  const creditMode = r.enforce_credit_limit ?? 'warning';

  return (
    <div className="space-y-3">
      <h2 className="flex items-center gap-2 text-lg font-semibold">
        <SlidersHorizontal className="h-4 w-4" /> System Rules
      </h2>
      <RuleRow title="Enforce Invoice Upload" hint="Require invoice on purchase">
        <Toggle label="Enforce Invoice Upload" on={r.enforce_invoice_upload === 'true'} disabled={mut.isPending} onChange={(v) => mut.mutate({ key: 'enforce_invoice_upload', value: v ? 'true' : 'false' })} />
      </RuleRow>
      <RuleRow title="Allow Partial Payment" hint="Allow payments less than balance">
        <Toggle label="Allow Partial Payment" on={r.allow_partial_payment === 'true'} disabled={mut.isPending} onChange={(v) => mut.mutate({ key: 'allow_partial_payment', value: v ? 'true' : 'false' })} />
      </RuleRow>
      <RuleRow title="Enforce Credit Limit" hint={`Current: ${creditMode}`}>
        <div className="flex overflow-hidden rounded-md border">
          {(['strict', 'warning'] as const).map((m) => (
            <button
              key={m} type="button" disabled={mut.isPending}
              onClick={() => mut.mutate({ key: 'enforce_credit_limit', value: m })}
              className={cn('px-3 py-1.5 text-sm capitalize transition-colors', creditMode === m ? 'bg-primary text-primary-foreground' : 'bg-card text-muted-foreground hover:bg-muted')}
            >
              {m}
            </button>
          ))}
        </div>
      </RuleRow>
      <RuleRow title="Enable GST Auto Calculation" hint="Auto calculate 18% GST">
        <Toggle label="Enable GST Auto Calculation" on={r.enable_gst_auto_calc === 'true'} disabled={mut.isPending} onChange={(v) => mut.mutate({ key: 'enable_gst_auto_calc', value: v ? 'true' : 'false' })} />
      </RuleRow>
    </div>
  );
}

// ─── Budget ───────────────────────────────────────────────────────────────────────

function BudgetTab() {
  const { data = [], isLoading } = useQuery({
    queryKey: ['finance', 'budget'],
    queryFn: () => financeService.getBudget(),
  });

  if (isLoading) return <div className="grid gap-4 sm:grid-cols-2">{Array.from({ length: 2 }).map((_, i) => <div key={i} className="h-28 animate-pulse rounded-xl bg-muted/40" />)}</div>;
  if (data.length === 0) {
    return <div className="rounded-xl border bg-card p-8 text-center text-sm text-muted-foreground">No categories with budget limits configured.</div>;
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {(data as BudgetRow[]).map((b) => {
        const over = b.pct >= 100;
        const warn = b.pct >= 80 && b.pct < 100;
        return (
          <div key={b.id} className={cn('rounded-xl border bg-card p-5', over && 'border-rose-300', warn && 'border-amber-300')}>
            <div className="flex items-center justify-between">
              <p className="font-semibold">{b.name}</p>
              {over && <Badge variant="destructive">Over</Badge>}
              {warn && <Badge variant="warning">Warning</Badge>}
            </div>
            <div className="mt-3 h-2 overflow-hidden rounded-full bg-muted">
              <div className={cn('h-full rounded-full', over ? 'bg-rose-500' : warn ? 'bg-amber-500' : 'bg-primary')} style={{ width: `${Math.min(b.pct, 100)}%` }} />
            </div>
            <div className="mt-2 flex justify-between text-xs text-muted-foreground">
              <span>Used: {formatINR(b.used)}</span>
              <span>Budget: {formatINR(b.budget)}</span>
            </div>
            <p className="mt-1 text-sm text-primary">Remaining: {formatINR(b.remaining)} ({100 - b.pct < 0 ? 0 : 100 - b.pct}%)</p>
          </div>
        );
      })}
    </div>
  );
}

// ─── Audit Logs ─────────────────────────────────────────────────────────────────────

function AuditLogsTab() {
  const [page, setPage] = useState(1);
  const limit = 15;
  const { data, isLoading, isError } = useQuery({
    queryKey: ['finance', 'audit', { page, limit }],
    queryFn: () => financeService.listAuditLogs({ page, limit }),
  });

  const columns: Column<FinanceAuditLog>[] = [
    { key: 'date', header: 'Date', cell: (r) => <span className="whitespace-nowrap text-sm">{formatDateTime(r.created_at)}</span> },
    { key: 'module', header: 'Module', cell: (r) => <Badge variant="default">{r.module}</Badge> },
    { key: 'action', header: 'Action', cell: (r) => r.action },
    { key: 'by', header: 'By', cell: (r) => r.action_by_name?.trim() || 'System' },
  ];

  const pagination = data
    ? { page: data.pagination.page, totalPages: data.pagination.totalPages, total: data.pagination.total, onPageChange: setPage }
    : undefined;

  return (
    <>
      <h2 className="mb-4 text-lg font-semibold">Audit Logs</h2>
      <DataTable columns={columns} rows={data?.data ?? []} rowKey={(r) => r.id} loading={isLoading} error={isError} empty="No audit entries yet." pagination={pagination} />
    </>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────────

export function MasterPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') ?? 'categories';
  const setTab = (key: string) => setSearchParams({ tab: key });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">Finance Master</h1>
        <p className="text-sm text-muted-foreground">System configuration and financial controls</p>
      </div>

      <Tabs tabs={TABS} active={tab} onChange={setTab} />

      <div className="mt-2">
        {tab === 'categories' && <CategoriesTab />}
        {tab === 'modes' && <PaymentModesTab />}
        {tab === 'rules' && <SystemRulesTab />}
        {tab === 'budget' && <BudgetTab />}
        {tab === 'audit' && <AuditLogsTab />}
      </div>
    </div>
  );
}
